import type { SourceTypeFilter } from "@/lib/sources";
import { Role } from "@/lib/supabase";
import {
  Activity,
  BadgeCheck,
  Blocks,
  Bot,
  Brain,
  CircleUserRound,
  ClipboardList,
  Coins,
  Cpu,
  FlaskConical,
  Gauge,
  Globe,
  HeartPulse,
  Import,
  KeyRound,
  Library,
  type LucideIcon,
  MessageSquare,
  MessageSquareWarning,
  Network,
  Palette,
  RotateCcw,
  ScrollText,
  Server,
  Settings,
  Stamp,
  Users,
  Workflow,
  Wrench,
} from "lucide-react";

export const USER_MEMORY_PATH = "/account/memory";

export interface SettingsTab {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: readonly Role[];
  /** Keep the tab highlighted on its nested routes (a detail page under it). */
  nested?: boolean;
}

/** Source type filters shown under Settings → Sources in the sidebar. */
export interface SourcesSubTab {
  value: Exclude<SourceTypeFilter, "all">;
  label: string;
}

export const SOURCES_SUBTABS: readonly SourcesSubTab[] = [
  // { value: "all", label: "todas" }, // hidden — kept for future re-enable.
  { value: "upload", label: "documentos" },
  { value: "url", label: "urls" },
  { value: "notion", label: "notion" },
  { value: "s3", label: "s3 repos" },
  { value: "shortcut", label: "shortcut" },
  { value: "postman", label: "postman" },
  { value: "self", label: "Sobre el RAG" },
  { value: "chat", label: "chat" },
  { value: "jira", label: "jira" },
  { value: "feedback", label: "Feedback" },
];

const VALID_SOURCES_SUBTABS: ReadonlySet<string> = new Set(SOURCES_SUBTABS.map((t) => t.value));

export function isValidSourcesSubTab(
  value: string | null,
): value is Exclude<SourceTypeFilter, "all"> {
  return value !== null && VALID_SOURCES_SUBTABS.has(value);
}

export type PrimarySection = "chat" | "graph" | "settings" | "account" | "api-reference";

/** Derive the active primary product area from the current pathname. */
export function activePrimarySection(pathname: string): PrimarySection {
  if (pathname === "/" || pathname === "/chat" || pathname.startsWith("/chat/")) {
    return "chat";
  }
  if (pathname.startsWith("/api-reference")) return "api-reference";
  if (pathname === "/graph" || pathname.startsWith("/graph/")) {
    return "graph";
  }
  if (pathname === "/account" || pathname.startsWith("/account/")) {
    return "account";
  }
  if (pathname === "/settings" || pathname.startsWith("/settings/")) {
    return "settings";
  }
  return "chat";
}

/**
 * Admin/system settings tabs — personal and graph pages live under `/account`
 * and `/graph`. Display order: health on top (the admin landing page), then
 * quality, then content pipeline (the contributor-visible tabs) → assistant
 * behavior & connections → ops/monitoring → platform config. Role filtering
 * preserves relative order.
 */
export const SETTINGS_TABS: readonly SettingsTab[] = [
  { to: "/settings/health", label: "salud", icon: HeartPulse, roles: [Role.Admin] },
  { to: "/settings/knowledge-quality", label: "calidad", icon: BadgeCheck, roles: [Role.Admin] },
  {
    to: "/settings/sources",
    label: "fuentes",
    icon: Import,
    roles: [Role.Contributor, Role.Admin],
  },
  {
    to: "/settings/knowledge",
    label: "aprobar conocimiento",
    icon: Stamp,
    roles: [Role.Contributor, Role.Admin],
  },
  // Story preparations are used by a couple of people, so they live here with
  // the content tabs a contributor already sees rather than in the primary
  // navigation. Nested: a preparation opens at `/settings/story-preparations/:id`.
  {
    to: "/settings/story-preparations",
    label: "historias",
    icon: ClipboardList,
    roles: [Role.Contributor, Role.Admin],
    nested: true,
  },
  {
    to: "/settings/knowledge-repositories",
    label: "repositorios de conocimiento",
    icon: Library,
    roles: [Role.Admin],
  },
  { to: "/settings/feedback", label: "feedback", icon: MessageSquareWarning, roles: [Role.Admin] },
  { to: "/settings/agents", label: "agentes", icon: Bot, roles: [Role.Admin] },
  {
    to: "/settings/web-test-targets",
    label: "targets de prueba web",
    icon: Globe,
    roles: [Role.Admin],
  },
  { to: "/settings/integrations", label: "integraciones", icon: Blocks, roles: [Role.Admin] },
  {
    to: "/settings/live-tools",
    label: "consultas en vivo",
    icon: Wrench,
    roles: [Role.Admin],
  },
  { to: "/settings/usage", label: "costos", icon: Coins, roles: [Role.Admin] },
  {
    to: "/settings/system-tasks",
    label: "tareas del sistema",
    icon: Workflow,
    roles: [Role.Admin],
  },
  { to: "/settings/dlq", label: "reintentos", icon: RotateCcw, roles: [Role.Admin] },
  { to: "/settings/audit", label: "audit", icon: ScrollText, roles: [Role.Admin] },
  { to: "/settings/users", label: "usuarios", icon: Users, roles: [Role.Admin] },
  { to: "/settings/models", label: "modelos", icon: Cpu, roles: [Role.Admin] },
  { to: "/settings/benchmarks", label: "benchmarks", icon: FlaskConical, roles: [Role.Admin] },
  { to: "/settings/rate-limits", label: "rate limits", icon: Gauge, roles: [Role.Admin] },
  { to: "/settings/system", label: "sistema", icon: Server, roles: [Role.Admin] },
];

/** Fallback when the role has no settings tabs (should not reach `/settings`). */
const DEFAULT_SETTINGS_FALLBACK = "/settings/sources";

export interface AccountTab {
  to: string;
  label: string;
  icon: LucideIcon;
}

export const ACCOUNT_TABS: readonly AccountTab[] = [
  { to: USER_MEMORY_PATH, label: "mi memoria", icon: Brain },
  { to: "/account/api-tokens", label: "api tokens", icon: KeyRound },
  { to: "/account/appearance", label: "apariencia", icon: Palette },
  { to: "/account/activity", label: "mi actividad", icon: Activity },
];

export interface PrimaryNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** When true, the item is shown only if the role can access settings. */
  requiresSettingsAccess?: boolean;
  requiresApiReferenceFlag?: boolean;
}

export const PRIMARY_NAV: readonly PrimaryNavItem[] = [
  { to: "/", label: "chat", icon: MessageSquare, end: true },
  { to: "/api-reference", label: "docs", icon: Library, requiresApiReferenceFlag: true },
  { to: "/graph", label: "grafo", icon: Network },
  { to: "/settings", label: "settings", icon: Settings, requiresSettingsAccess: true },
  { to: "/account", label: "mi cuenta", icon: CircleUserRound },
];

export function settingsTabsForRole(role: Role): readonly SettingsTab[] {
  return SETTINGS_TABS.filter((tab) => tab.roles.includes(role));
}

/** First settings page visible for the role — drives `/settings` index redirect. */
export function defaultSettingsPath(role: Role): string {
  return settingsTabsForRole(role)[0]?.to ?? DEFAULT_SETTINGS_FALLBACK;
}

export function hasSettingsAccess(role: Role): boolean {
  return settingsTabsForRole(role).length > 0;
}

export function primaryNavForRole(role: Role): readonly PrimaryNavItem[] {
  return PRIMARY_NAV.filter((item) => !item.requiresSettingsAccess || hasSettingsAccess(role));
}

/** Destinations that share the top row of the primary nav (option C: 3 + 2). */
const PRIMARY_WORK_DESTINATIONS: ReadonlySet<string> = new Set(["/", "/api-reference", "/graph"]);

/**
 * Split the visible primary nav into the work row (chat, docs, grafo) and
 * the account row (settings, mi cuenta). Hidden items are already removed,
 * so a row can be shorter than the full set.
 */
export function primaryNavRows(
  items: readonly PrimaryNavItem[],
): readonly (readonly PrimaryNavItem[])[] {
  const work = items.filter((item) => PRIMARY_WORK_DESTINATIONS.has(item.to));
  const account = items.filter((item) => !PRIMARY_WORK_DESTINATIONS.has(item.to));
  return [work, account].filter((row) => row.length > 0);
}

/** Column count for one primary-nav row. A single item spans the sidebar. */
export function primaryNavRowClass(count: number): "grid-cols-1" | "grid-cols-2" | "grid-cols-3" {
  if (count >= 3) return "grid-cols-3";
  if (count === 2) return "grid-cols-2";
  return "grid-cols-1";
}

export function isPrimaryNavActive(pathname: string, item: PrimaryNavItem): boolean {
  if (item.to === "/") {
    // Chat lives at `/`, bare `/chat`, and `/chat/:sessionId`. The link
    // itself points at `/` with `end`, so a session URL is not NavLink-active.
    return pathname === "/" || pathname === "/chat" || pathname.startsWith("/chat/");
  }
  if (item.to === "/account") {
    return pathname === "/account" || pathname.startsWith("/account/");
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
